import useSWR from 'swr';
import { RefreshCw } from 'lucide-react';
import { getCreditBalance, getCreditHistory, type CreditTransaction } from '@/api/pricing';
import type { EngineAuthContext } from '@/lib/auth';

// Description recognition is temporary until producers expose structured causes.
// Full producer forms and compatible types are guarded by source-coupled tests.
const identifier = '[A-Za-z0-9][A-Za-z0-9._:/-]*';
const form = (pattern: string) => new RegExp(`^${pattern}$`);
const usageForms: Array<[RegExp, string]> = [
  [form(`Managed research (stage1|stage4) job ${identifier}`), 'Research job fee'],
  [form(`Agent-assisted Stage 1 analysis ${identifier} \\(${identifier}\\)`), 'Research analysis fee'],
  [form(`Hosted MAMMAL inference ${identifier} \\(${identifier}\\)`), 'Hosted research model fee'],
  [form(`Managed inference ${identifier} — ${identifier}( \\(fallback\\))?`), 'Managed inference charge'],
  [form(`Managed vertical assistant ${identifier}:${identifier} — plan ${identifier}`), 'Assistant credit reservation'],
];
const refundForms: Array<[RegExp, string]> = [
  [form(`Managed assistant failure refund — plan ${identifier}`), 'Assistant refund after a failed request'],
  [form(`Managed assistant settlement refund — plan ${identifier}`), 'Assistant refund'],
  [form(`Managed assistant estimate true-up — plan ${identifier}`), 'Assistant estimate adjustment'],
];
const categories: Record<string, string> = {
  promotion: 'Promotional credits', purchase: 'Purchased credits',
  admin_adjustment: 'Account adjustment', share_reward: 'Sharing reward',
  llm_usage_refund: 'Usage refund', video_render: 'Video rendering',
  expiration: 'Expired credits', marketplace_purchase: 'Purchase', marketplace_sale: 'Sale credit',
  hold: 'Credit reservation', hold_release: 'Reservation release', hold_refund: 'Reservation refund',
  review_bounty: 'Review bounty', review_reward: 'Review reward', review_bounty_refund: 'Review refund',
};
function cause(row: CreditTransaction): string {
  const description = row.description || '';
  if (row.transaction_type === 'subscription_grant') {
    if (description === 'Welcome credit') return 'Welcome credit';
    return form(`Free tier activation — ${identifier}`).test(description)
      ? 'Starter credit grant' : 'Plan credit grant';
  }
  const forms = row.transaction_type === 'llm_usage' ? usageForms
    : row.transaction_type === 'llm_usage_refund' ? refundForms : [];
  const match = forms.find(([pattern]) => pattern.test(description));
  if (match) {
    if (match[1] === 'Research job fee') {
      const stage = description.match(/research stage([14]) job/)?.[1];
      return `Research job fee — Stage ${stage}`;
    }
    return match[1] === 'Managed inference charge' && description.endsWith(' (fallback)')
      ? `${match[1]} — Fallback price applied` : match[1];
  }
  const category = (Object.prototype.hasOwnProperty.call(categories, row.transaction_type) ? categories[row.transaction_type] : undefined)
    || (row.amount_usd < 0 ? 'Charge' : row.amount_usd > 0 ? 'Credit added' : 'Credit activity');
  return `${category} — specific cause not recorded.`;
}
function usd(amount: number, signed = false): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 6,
    signDisplay: signed ? 'always' : 'auto',
  }).format(amount);
}
function credits(amount: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(amount);
}
function date(iso: string | null): string {
  const value = iso ? new Date(iso) : null;
  return !value || Number.isNaN(value.getTime()) ? 'Date unavailable'
    : value.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function reference(id: string, index: number): string {
  // Ledger IDs are UUIDs. Never echo arbitrary internal identifiers as copy.
  return /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(id) ? id.slice(0, 8) : `Entry ${index + 1}`;
}

export function CreditsStatement({ verticalId, context, accountName }: {
  verticalId: string; context: EngineAuthContext; accountName: string;
}) {
  const enabled = Boolean(context.token && context.activeTenantId);
  const options = { revalidateOnFocus: false, revalidateOnReconnect: false, shouldRetryOnError: false };
  const balance = useSWR(enabled ? ['statement/balance', verticalId] : null,
    () => getCreditBalance(verticalId, context), options);
  const history = useSWR(enabled ? ['statement/history', verticalId] : null,
    () => getCreditHistory(verticalId, 200, context), options);
  if (!enabled) return <p>Choose an account to view its credit statement.</p>;
  const retry = () => {
    if (balance.error) void balance.mutate();
    if (history.error) void history.mutate();
  };
  const refresh = () => { void balance.mutate(); void history.mutate(); };
  return (
    <section className="sp-widget mt-6" aria-label="Credit statement">
      <div className="sp-widget-header flex flex-wrap items-center justify-between gap-3">
        <div><h2>Credit statement</h2><p className="text-sm text-[var(--sp-text-muted)]">{accountName} · {verticalId}</p></div>
        <button className="sp-btn" onClick={refresh} disabled={balance.isValidating || history.isValidating}>
          <RefreshCw size={14} aria-hidden="true" /> Refresh
        </button>
      </div>
      <div className="sp-widget-body space-y-4">
        {(balance.isLoading || history.isLoading) && <p role="status">Loading your credit statement…</p>}
        {balance.error && <p role="alert">{balance.data ? 'Could not refresh your balance. Showing previously loaded balance.' : 'Your balance could not be loaded.'}</p>}
        {balance.data && <div>
          <h3>Available balance</h3>
          <p className="text-xl">{usd(balance.data.balance_usd)} USD · {credits(balance.data.balance_credits)} credits</p>
          <p>Allowance: {credits(balance.data.expiring_credits)} credits · expires {date(balance.data.expires_at)}</p>
          <p>Non-expiring credits: {credits(balance.data.permanent_credits)}</p>
        </div>}
        {history.error && <p role="alert">{history.data ? 'Could not refresh your credit activity. Showing previously loaded activity.' : 'Your credit activity could not be loaded.'}</p>}
        {(balance.error || history.error) && <button className="sp-btn" onClick={retry} disabled={balance.isValidating || history.isValidating}>Retry</button>}
        {history.data && <div>
          <h3>Recorded activity</h3>
          {history.data.length === 0 ? <p>No credit activity recorded for this account here.</p> : <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead><tr><th className="p-2">Date</th><th className="p-2">Cause</th><th className="p-2">USD amount</th><th className="p-2">Reference</th></tr></thead>
              <tbody>{history.data.map((row, index) => <tr key={row.id} className="border-t border-[var(--sp-border)]">
                <td className="p-2">{date(row.created_at)}</td><td className="p-2">{cause(row)}</td>
                <td className="p-2 whitespace-nowrap font-mono">{usd(row.amount_usd, true)}</td><td className="p-2 font-mono">{reference(row.id, index)}</td>
              </tr>)}</tbody>
            </table>
          </div>}
          {history.data.length >= 200 && <p>Showing the latest 200 entries. Older activity may not be shown.</p>}
          <p className="text-sm text-[var(--sp-text-muted)]">Recorded activity may include expired credits. Available balance and activity are loaded separately.</p>
        </div>}
      </div>
    </section>
  );
}
